import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase, Usuario } from '@/lib/supabase';

export interface User {
  id: string;
  nombre: string;
  usuario: string;
  rol: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (usuario: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => ({ success: false, message: '' }),
  logout: () => {},
  isSuperAdmin: false,
});

const USER_DOMAIN = '@autorepuestos.com';

async function loadUserProfile(username: string): Promise<User | null> {
  const { data } = await supabase
    .from('usuarios')
    .select('*')
    .eq('usuario', username)
    .maybeSingle();

  if (!data) return null;

  return {
    id: data.id,
    nombre: data.nombre,
    usuario: data.usuario,
    rol: data.rol,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const email = session.user.email || '';
        const username = email.split('@')[0];

        loadUserProfile(username).then((profile) => {
          if (profile) setUser(profile);
        }).finally(() => {
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        const email = session.user.email || '';
        const username = email.split('@')[0];

        loadUserProfile(username).then((profile) => {
          if (profile) setUser(profile);
        });
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = useCallback(async (usuario: string, password: string) => {
    setIsLoading(true);

    try {
      const email = `${usuario}${USER_DOMAIN}`;

      // Step 1: Try to sign in with existing credentials
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!signInError && signInData?.user) {
        let profile = await loadUserProfile(usuario);

        if (!profile) {
          await supabase.from('usuarios').insert({
            nombre: usuario,
            usuario,
            rol: 'usuario',
          });
          profile = await loadUserProfile(usuario);
        }

        if (profile) {
          setUser(profile);
          setIsLoading(false);
          return { success: true, message: 'Inicio de sesión exitoso' };
        }
      }

      // Step 2: If sign-in failed, check if user exists in auth at all
      if (signInError) {
        const isInvalidCredentials = signInError.message.includes('Invalid login credentials');
        const isEmailNotConfirmed = signInError.message.includes('Email not confirmed');

        if (isEmailNotConfirmed) {
          const { error: confirmError } = await supabase.functions.invoke('auto-confirm-user', {
            body: { email },
          });

          if (!confirmError) {
            const { data: retryData, error: retryError } = await supabase.auth.signInWithPassword({
              email,
              password,
            });

            if (!retryError && retryData?.user) {
              let profile = await loadUserProfile(usuario);

              if (!profile) {
                await supabase.from('usuarios').insert({
                  nombre: usuario,
                  usuario,
                  rol: 'usuario',
                });
                profile = await loadUserProfile(usuario);
              }

              if (profile) {
                setUser(profile);
                setIsLoading(false);
                return { success: true, message: 'Inicio de sesión exitoso' };
              }
            }
          }

          setIsLoading(false);
          return {
            success: false,
            message: 'Usuario o contraseña incorrectos',
          };
        }

        if (isInvalidCredentials) {
          // User might not exist yet - try to sign up
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: { nombre: usuario, usuario },
            },
          });

          if (signUpError) {
            setIsLoading(false);
            return { success: false, message: 'Usuario o contraseña incorrectos' };
          }

          // If signUp returned a session (email confirmation disabled in Supabase)
          if (signUpData?.session) {
            // Ensure profile exists in usuarios table
            const existingProfile = await loadUserProfile(usuario);
            if (!existingProfile) {
              await supabase.from('usuarios').insert({
                nombre: usuario,
                usuario,
                rol: 'usuario',
              });
            }

            const profile = await loadUserProfile(usuario);
            if (profile) {
              setUser(profile);
              setIsLoading(false);
              return { success: true, message: 'Cuenta creada. Inicio de sesión exitoso' };
            }
          }

          // signUp succeeded but requires email confirmation
          if (signUpData?.user && !signUpData?.session) {
            setIsLoading(false);
            return {
              success: false,
              message: 'Se ha enviado un email de confirmación. Por favor revise su bandeja de entrada.',
            };
          }

          setIsLoading(false);
          return { success: false, message: 'Usuario o contraseña incorrectos' };
        }
      }

      setIsLoading(false);
      return { success: false, message: 'Usuario o contraseña incorrectos' };
    } catch {
      setIsLoading(false);
      return { success: false, message: 'Error al conectar con el servidor' };
    }
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const isSuperAdmin = user?.rol === 'super_admin';
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
}